import {
  ApiError,
  apiClient,
  clearConsoleBffCsrfToken,
  consoleBffRequestUrl,
  getConsoleBffCsrfToken,
} from "./client";
import type { consoleBffAuth } from "@kintone-storage/openapi-types";
import type { UserRole } from "@/types/api";
import {captureChatReferral} from '@/lib/chat-referral';

export interface LoginRequest {
  email: string;
  password: string;
}

export type ExternalAuthProvider = "google" | "microsoft";
export type ExternalAuthIntent = "signup" | "login" | "link" | "reauthenticate";
export type LegalDocumentsDescriptor =
  consoleBffAuth.components["schemas"]["LegalDocumentsDescriptor"];
export type LocalSignupCompleteRequest =
  consoleBffAuth.components["schemas"]["LocalSignupCompleteRequest"];
export type SessionEstablishedResponse =
  consoleBffAuth.components["schemas"]["SessionEstablishedResponse"];
export type ExternalSignupDraftResponse =
  consoleBffAuth.components["schemas"]["ExternalSignupDraftResponse"];
export type ExternalSignupCompleteRequest =
  consoleBffAuth.components["schemas"]["ExternalSignupCompleteRequest"];
export type AuthMethodsResponse =
  consoleBffAuth.components["schemas"]["AuthMethodsResponse"];
export type AccountDeletionCheckResponse =
  consoleBffAuth.components["schemas"]["AccountDeletionCheckResponse"];
export type CustomerInvitationPreviewResponse =
  consoleBffAuth.components["schemas"]["CustomerInvitationPreviewResponse"];
export type CustomerInvitationAcceptResponse =
  consoleBffAuth.components["schemas"]["CustomerInvitationAcceptResponse"];
export type MembershipDepartureCheckResponse =
  consoleBffAuth.components["schemas"]["MembershipDepartureCheckResponse"];

export interface CurrentMembership {
  customerId: string;
  customerUserId: string;
  displayName: string;
  role: UserRole;
}

export interface CustomerMembership extends CurrentMembership {
  customerName: string;
  organizationPhoneNumber: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string | null;
  userState: "active" | "disabled" | "deleted" | string;
}

export interface ConsoleServiceSubscriptionSummary {
  customerId: string;
  kintoneConnectionState: "unregistered" | "registered";
  kintoneSubdomain: string | null;
  serviceSubscriptionState: string;
  commercialState: string;
  resourceReleaseState: "not_required" | "pending" | "completed" | "manual_repair_required";
}

export interface MeResponse {
  accountId: string;
  email: string;
  fullName: string;
  currentMembership: CurrentMembership | null;
  memberships: CustomerMembership[];
  accountSetupState: "organization_required" | "organization_selected";
  serviceSubscription: ConsoleServiceSubscriptionSummary | null;
}

export interface CustomerSwitchResponse {
  accountId: string;
  currentMembership: CurrentMembership;
}

export interface CustomerCreateRequest {
  customerName: string;
  organizationPhoneNumber: string;
  postalCode: string;
  addressLine1: string;
  addressLine2?: string | null;
  legalDocumentsAccepted: true;
  serviceTermsVersion: string;
  cookiePolicyVersion: string;
  termsAndPoliciesVersion: string;
}

export interface ServiceLegalDocumentsDescriptor {
  releasePhase: "beta" | "formal";
  serviceTerms: { version: string; url: string };
  cookiePolicy: { version: string; url: string };
  termsAndPolicies: { version: string; url: string };
}

export type CustomerCreateResponse = CustomerSwitchResponse;

export interface CurrentCustomerUpdateRequest {
  customerName: string;
  organizationPhoneNumber: string;
  postalCode: string;
  addressLine1: string;
  addressLine2?: string | null;
}

export interface CurrentCustomerUpdateResponse {
  customerId: string;
  customerName: string;
  organizationPhoneNumber: string;
  postalCode: string;
  addressLine1: string;
  addressLine2: string | null;
  currentMembership: CurrentMembership;
}

export type OrganizationDeleteBlockerCode =
  | "active_service_subscription_exists"
  | "subscription_payment_required"
  | "pending_billing_operation_exists"
  | "unresolved_billing_obligation_exists"
  | "delete_already_in_progress";

export interface OrganizationDeleteBlocker {
  code: OrganizationDeleteBlockerCode;
  message: string;
}

export interface CurrentCustomerDeletionCheckResponse {
  customerId: string;
  customerName: string;
  customerState: string;
  deletable: boolean;
  blockers: OrganizationDeleteBlocker[];
  erasureSummary: {
    state: "not_requested" | "scheduled" | string;
    requestedAt: string | null;
    dueAt: string | null;
    completedAt: string | null;
    failureReasonCode: string | null;
  };
  checkedAt: string;
}

export interface CurrentCustomerDeleteRequest {
  confirmationCustomerName: string;
  confirmLocalGatewayRoutesDelete: true;
}

export interface CurrentCustomerDeleteResponse {
  deletedCustomerId: string;
  erasureState: "scheduled" | string;
  nextAccountSetupState: "organization_required" | "organization_selected";
  nextMembership: CustomerMembership | null;
}

export async function loginLocal(body: LoginRequest): Promise<SessionEstablishedResponse | TotpChallengeResponse> {
  return publicAuthRequest<SessionEstablishedResponse | TotpChallengeResponse>("/console-bff/auth/login/local", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function fetchAccountLegalDocuments(): Promise<LegalDocumentsDescriptor> {
  return publicAuthRequest<LegalDocumentsDescriptor>("/console-bff/auth/account-legal-documents");
}

export function startSignupEmail(email: string): Promise<void> {
  const chatReferralId=captureChatReferral();
  return publicAuthRequest<void>("/console-bff/auth/signup/email/start", {
    method: "POST",
    body: JSON.stringify({ email, ...(chatReferralId?{chatReferralId}:{}) }),
  });
}

export function fetchSignupState(): Promise<{ expiresAt: string }> {
  return publicAuthRequest("/console-bff/auth/signup/state");
}

export function verifySignupEmail(token: string): Promise<void> {
  return publicAuthRequest<void>("/console-bff/auth/signup/email/verify", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function completeLocalSignup(
  body: LocalSignupCompleteRequest,
): Promise<SessionEstablishedResponse> {
  return publicAuthRequest<SessionEstablishedResponse>(
    "/console-bff/auth/signup/local/complete",
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function startExternalAuthentication(
  provider: ExternalAuthProvider,
  body: { intent: ExternalAuthIntent },
) {
  const chatReferralId=body.intent==='signup'?captureChatReferral():undefined;
  const input={...body,...(chatReferralId?{chatReferralId}:{})};
  sessionStorage.setItem("kdrive.auth.intent", body.intent);
  sessionStorage.setItem("kdrive.auth.provider", provider);
  const path = `/console-bff/auth/external/${provider}/start`;
  try {
    const result = body.intent === "link" || body.intent === "reauthenticate"
      ? await apiClient<{ authorizationUrl: string }>(path, {
          method: "POST",
          body: JSON.stringify(input),
        })
      : await publicAuthRequest<{ authorizationUrl: string }>(path, {
          method: "POST",
          body: JSON.stringify(input),
        });
    globalThis.location.assign(result.authorizationUrl);
  } catch (error) {
    sessionStorage.removeItem("kdrive.auth.intent");
    sessionStorage.removeItem("kdrive.auth.provider");
    throw error;
  }
}

export function startLocalRecovery(email: string): Promise<void> {
  return publicAuthRequest<void>("/console-bff/auth/local-recovery/start", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function completeLocalRecovery(token: string, newPassword: string): Promise<void> {
  return publicAuthRequest<void>("/console-bff/auth/local-recovery/complete", {
    method: "POST",
    body: JSON.stringify({ token, newPassword }),
  });
}

export async function logout(): Promise<void> {
  let proof: string | null = null;
  try {
    proof = await getConsoleBffCsrfToken();
  } catch {
    // Missing, malformed, expired, or DB-invalid session cookies cannot issue
    // a proof, but the logout endpoint still permits clearing stale cookies.
  }
  try {
    const res = await fetch(consoleBffRequestUrl("/console-bff/auth/logout"), {
      method: "POST",
      headers: proof ? { "x-csrf-token": proof } : {},
      credentials: "include",
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        code?: string;
        message?: string;
        requestId?: string;
      };
      throw new ApiError(
        res.status,
        body.code ?? "UNKNOWN",
        body.message ?? res.statusText,
        body.requestId,
      );
    }
  } finally {
    clearConsoleBffCsrfToken();
  }
}

export function createRecentReauthentication(password: string) {
  return apiClient<{ reauthenticatedAt: string; expiresAt: string }>(
    "/console-bff/auth/re-authentications/local",
    {
      method: "POST",
      body: JSON.stringify({ password }),
    },
  );
}

export function fetchAuthMethods(): Promise<AuthMethodsResponse> {
  return apiClient<AuthMethodsResponse>("/console-bff/auth/methods");
}

export function enableLocalAuthMethod(newPassword: string): Promise<void> {
  return apiClient<void>("/console-bff/auth/methods/local", {
    method: "POST",
    body: JSON.stringify({ newPassword }),
  });
}

export function disableAuthMethod(provider: "local" | ExternalAuthProvider): Promise<void> {
  return apiClient<void>(`/console-bff/auth/methods/${provider}`, { method: "DELETE" });
}

export function changePassword(newPassword: string): Promise<void> {
  return apiClient<void>("/console-bff/auth/password/change", {
    method: "POST",
    body: JSON.stringify({ newPassword }),
  });
}

export function startAccountEmailChange(email: string): Promise<void> {
  return apiClient<void>("/console-bff/auth/account-email/change/start", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function completeAccountEmailChange(token: string): Promise<void> {
  return apiClient<void>("/console-bff/auth/account-email/change/complete", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export function cancelExternalSignup(): Promise<void> {
  return publicAuthRequest<void>("/console-bff/auth/signup/external/draft", { method: "DELETE" });
}

export function fetchExternalSignupDraft(): Promise<ExternalSignupDraftResponse> {
  return publicAuthRequest<ExternalSignupDraftResponse>("/console-bff/auth/signup/external/draft");
}

export function startExternalSignupEmail(email: string): Promise<void> {
  return publicAuthRequest<void>("/console-bff/auth/signup/external/email/start", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function verifyExternalSignupEmail(token: string, confirmed = false): Promise<consoleBffAuth.components["schemas"]["ExternalSignupEmailVerifyResponse"]> {
  return publicAuthRequest<consoleBffAuth.components["schemas"]["ExternalSignupEmailVerifyResponse"]>("/console-bff/auth/signup/external/email/verify", {
    method: "POST",
    body: JSON.stringify({ token, confirmed }),
  });
}

export function completeExternalSignup(
  body: ExternalSignupCompleteRequest,
): Promise<SessionEstablishedResponse> {
  return publicAuthRequest<SessionEstablishedResponse>("/console-bff/auth/signup/external/complete", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function fetchAccountDeletionCheck(): Promise<AccountDeletionCheckResponse> {
  return apiClient<AccountDeletionCheckResponse>("/console-bff/auth/account/deletion-check");
}

export function deleteAccount(): Promise<void> {
  return apiClient<void>("/console-bff/auth/account", {
    method: "DELETE",
    body: JSON.stringify({ confirmation: "アカウントを削除" }),
  });
}

export function previewCustomerInvitation(invitationToken: string): Promise<CustomerInvitationPreviewResponse> {
  return apiClient<CustomerInvitationPreviewResponse>("/console-bff/auth/customer-invitations/preview", {
    method: "POST",
    body: JSON.stringify({ invitationToken }),
  });
}

export function acceptCustomerInvitation(invitationToken: string): Promise<CustomerInvitationAcceptResponse> {
  return apiClient<CustomerInvitationAcceptResponse>("/console-bff/auth/customer-invitations/accept", {
    method: "POST",
    body: JSON.stringify({ invitationToken }),
  });
}

export function declineCustomerInvitation(invitationToken: string): Promise<void> {
  return apiClient<void>("/console-bff/auth/customer-invitations/decline", {
    method: "POST",
    body: JSON.stringify({ invitationToken }),
  });
}

export function fetchCurrentMembershipDepartureCheck(): Promise<MembershipDepartureCheckResponse> {
  return apiClient<MembershipDepartureCheckResponse>("/console-bff/auth/current-membership/departure-check");
}

export function departCurrentMembership(): Promise<void> {
  return apiClient<void>("/console-bff/auth/current-membership", {
    method: "DELETE",
    body: JSON.stringify({ confirmation: "この組織から退出" }),
  });
}

async function publicAuthRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(consoleBffRequestUrl(path), {
    ...options,
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(options.headers as Record<string, string> | undefined),
    },
    credentials: "include",
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
      requestId?: string;
      retryable?: boolean;
      diagnostic?: unknown;
    };
    const retryAfter = response.headers.get("retry-after");
    const retryAfterSeconds = retryAfter && /^[1-9][0-9]*$/u.test(retryAfter)
      ? Math.min(Number(retryAfter), 900)
      : undefined;
    throw new ApiError(
      response.status,
      body.code ?? "UNKNOWN",
      body.message ?? response.statusText,
      body.requestId,
      { diagnostic: body.diagnostic },
      body.retryable,
      retryAfterSeconds,
    );
  }
  if (response.status === 204 || (response.status === 202 && path !== "/console-bff/auth/login/local")) return undefined as T;
  return await response.json() as T;
}

export async function switchCustomer(customerId: string): Promise<CustomerSwitchResponse> {
  return await apiClient<CustomerSwitchResponse>("/console-bff/auth/customer-switches", {
    method: "POST",
    body: JSON.stringify({ customerId }),
  });
}

let pendingOrganizationCreateCommand:
  | { fingerprint: string; idempotencyKey: string }
  | null = null;

export async function createCustomer(
  body: CustomerCreateRequest,
): Promise<CustomerCreateResponse> {
  const fingerprint = JSON.stringify(body);
  const storageKey = "kdrive.organization-create-command";
  let idempotencyKey = pendingOrganizationCreateCommand?.fingerprint === fingerprint
    ? pendingOrganizationCreateCommand.idempotencyKey
    : "";
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as
      | { fingerprint?: unknown; idempotencyKey?: unknown }
      | null;
    if (stored?.fingerprint === fingerprint && typeof stored.idempotencyKey === "string") {
      idempotencyKey = stored.idempotencyKey;
    }
  } catch {
    // Storage access is optional; the in-memory request still receives a valid key.
  }
  if (!idempotencyKey) {
    idempotencyKey = `organization-create:${globalThis.crypto.randomUUID()}`;
    pendingOrganizationCreateCommand = { fingerprint, idempotencyKey };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ fingerprint, idempotencyKey }));
    } catch {
      // A blocked sessionStorage must not weaken the server-side command contract.
    }
  }
  const result = await apiClient<CustomerCreateResponse>("/console-bff/auth/customers", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: fingerprint,
  });
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // The result is already committed; stale browser storage is harmless.
  }
  pendingOrganizationCreateCommand = null;
  return result;
}

export async function fetchServiceLegalDocuments(): Promise<ServiceLegalDocumentsDescriptor> {
  return await apiClient<ServiceLegalDocumentsDescriptor>("/console-bff/auth/service-legal-documents");
}

export async function updateCurrentCustomer(
  body: CurrentCustomerUpdateRequest,
): Promise<CurrentCustomerUpdateResponse> {
  return await apiClient<CurrentCustomerUpdateResponse>(
    "/console-bff/auth/current-customer",
    {
      method: "PATCH",
      body: JSON.stringify(body),
    },
  );
}

export async function fetchCurrentCustomerDeletionCheck():
  Promise<CurrentCustomerDeletionCheckResponse> {
  return await apiClient<CurrentCustomerDeletionCheckResponse>(
    "/console-bff/auth/current-customer/deletion-check",
  );
}

export async function deleteCurrentCustomer(
  body: CurrentCustomerDeleteRequest,
): Promise<CurrentCustomerDeleteResponse> {
  return await apiClient<CurrentCustomerDeleteResponse>(
    "/console-bff/auth/current-customer",
    {
      method: "DELETE",
      body: JSON.stringify(body),
    },
  );
}

export async function fetchMe(): Promise<MeResponse> {
  const res = await fetch(consoleBffRequestUrl("/console-bff/auth/me"), {
    credentials: "include",
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      code?: string;
      message?: string;
      requestId?: string;
      retryable?: boolean;
    };
    throw new ApiError(
      res.status,
      body.code ?? "UNKNOWN",
      body.message ?? res.statusText,
      body.requestId,
      undefined,
      body.retryable,
    );
  }
  return (await res.json()) as MeResponse;
}

export type TotpChallengeResponse = consoleBffAuth.components["schemas"]["TotpChallengeResponse"];
export type TotpStatus = consoleBffAuth.components["schemas"]["TotpStatusResponse"];
export type TotpSetup = consoleBffAuth.components["schemas"]["TotpSetupResponse"];
export type TotpProof = { code?: string; recoveryCode?: string };
export class TotpRequiredError extends Error {}
export function fetchTotpStatus(): Promise<TotpStatus> { return apiClient("/console-bff/auth/totp"); }
export function verifyTotpChallenge(proof: TotpProof): Promise<SessionEstablishedResponse | { reauthenticatedAt: string; expiresAt: string }> {
  return publicAuthRequest("/console-bff/auth/totp/verify", { method: "POST", body: JSON.stringify(proof) });
}
export function startTotpSetup(proof: TotpProof): Promise<TotpSetup> { return apiClient("/console-bff/auth/totp/setup", { method: "POST", body: JSON.stringify(proof) }); }
export function completeTotpSetup(token: string, code: string): Promise<{ recoveryCodes: string[] }> { return apiClient("/console-bff/auth/totp/setup/complete", { method: "POST", body: JSON.stringify({ token, code }) }); }
export function changeTotp(action: "disable" | "recovery-codes", proof: TotpProof): Promise<{ recoveryCodes?: string[] }> { return apiClient(`/console-bff/auth/totp/${action}`, { method: "POST", body: JSON.stringify(proof) }); }
